<template>
  <div>
    <v-toolbar
      color="primary"
      dark
    >
      <v-toolbar-title class="pl-11 pl-lg-0">
        {{ title }}
      </v-toolbar-title>
    </v-toolbar>
    <div class="wo-container">
      <v-row
        align="center"
        style="padding-bottom: 20px !important"
      >
        <v-col
          cols="auto"
          md="4"
          class="p-0"
          style="padding-left: 20px !important"
        >
          <h3 class="contract--number">
            {{ workOrder.contractNumber }}
          </h3>
        </v-col>
        <v-col class="p-0">
          <v-row>
            <ActionBar
              :isCompleted="isCompleted"
              :isLocked="isLocked"
              :reports="workOrder.reports"
              :workOrderId="workOrder.id || ''"
              :onClickOnLocation="onClickOnLocation"
              :canLeave="canLeave"
              :mutableIsReopened="isReopened"
              :onApprovePress="onApprovePress"
              :customDataCy="dataCy.workOrders.workOrderView.buttons.actionButtons"
              @loading="showLoadingWidget = $event"
              @toggle="isEditable = $event"
              @click-submit="onClickSubmit"
              @isReopened="checkIsReopened"
              @isCompleted="checkIsCompleted"
            />
          </v-row>
        </v-col>
      </v-row>
      <div class="wo-inner-container">
        <div class="wo-line" />
        <WorkOrderSection
          iconName="settings"
          :sectionName="sections.general"
          :isDisabled="isCompletedOrLocked"
          :showReportSection="summaryReport[sections.general]"
          @showHideSection="handleShowHideSection"
        >
          <template #child>
            <v-row>
              <v-col
                cols="12"
                md="8"
              >
                <VTextField
                  v-model="data.name"
                  outlined
                  dense
                  :label="$t('forms.labels.reportName')"
                  :dataCy="dataCy.workOrders.workOrderView.inputs.reportName"
                  :disabled="isCompletedOrLocked || !summaryReport[sections.general]"
                  @input="debouncedInput('name')"
                />
              </v-col>
              <v-col
                cols="12"
                md="4"
              >
                <VAutocomplete
                  v-model="data.type"
                  :label="$t('forms.labels.type')"
                  :loading="reportTypeLoading"
                  :items="displayedReportTypes"
                  required
                  outlined
                  dense
                  :error="hasNotType"
                  :disabled="isCompletedOrLocked || !summaryReport[sections.general]"
                  :messages="hasNotType ? $t('forms.labels.reportTypeError') : ''"
                  data-cy="woSummaryType"
                  :datacy="dataCy.workOrders.workOrderView.inputs.reportType"
                  @input="debouncedInput('type')"
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col
                cols="12"
                md="8"
              >
                <VTextField
                  :value="data.workOrderType"
                  outlined
                  dense
                  :label="$t('workOrders.workOrderType')"
                  :disabled="true"
                />
              </v-col>
            </v-row>
          </template>
        </WorkOrderSection>

        <WorkOrderSection
          iconName="info_outline"
          :sectionName="sections.general"
          :showReportSection="summaryReport[sections.general]"
          :isDisabled="isCompletedOrLocked"
          @showHideSection="handleShowHideSection"
        >
          <template #child>
            <WorkOrderForm
              :workOrder="workOrder"
              :isCompletedOrLocked="isCompletedOrLocked || !summaryReport[sections.general]"
            />
          </template>
        </WorkOrderSection>
        <WorkOrderSection
          iconName="schedule"
          :sectionName="sections.general"
          :showReportSection="summaryReport[sections.general]"
          :isDisabled="isCompletedOrLocked"
          @showHideSection="handleShowHideSection"
        >
          <template #child>
            <WorkOrderForm2
              :workOrder="workOrder"
              :isCompletedOrLocked="isCompletedOrLocked || !summaryReport[sections.general]"
            />
          </template>
        </WorkOrderSection>

        <WorkOrderSection
          v-if="!hiddenItemsByDefault.showOpeningComments"
          :iconName="'rate_review'"
          :sectionName="sections.openingComments"
          :showReportSection="summaryReport[sections.openingComments]"
          :isDisabled="isCompletedOrLocked"
          @showHideSection="handleShowHideSection"
        >
          <template #child>
            <v-row style="padding: 10px">
              <h3 :class="[!summaryReport[sections.openingComments] ? 'section--title disabled--text' : 'section--title']">
                {{ $t('forms.labels.openingComment') }}
              </h3>
            </v-row>
            <v-row style="padding: 0px 13px 20px 13px">
              <GlobalComment
                ref="openingComment"
                position="opening"
                :comment="lastOpeningComment"
                :canUploadImages="false"
                :isLab="false"
                :workOrderId="workOrder.id"
                :options="{disabled: isCompletedOrLocked || !summaryReport[sections.openingComments]}"
                :editorDataCy="dataCy.workOrders.workOrderView.inputs.openingComment"
                :saveBtnDataCy="dataCy.workOrders.workOrderView.buttons.saveComment"
                @commentChange="(message) => onCommentChanged(message, 'opening')"
                @onCommentSaved="unsavedComment = false"
              />
            </v-row>
          </template>
        </WorkOrderSection>

        <WorkOrderSection
          v-if="!hiddenItemsByDefault.showAnalyses"
          :iconName="'analysis'"
          :sectionName="sections.analyses"
          :showReportSection="summaryReport[sections.analyses]"
          :isDisabled="isCompletedOrLocked"
          @showHideSection="handleShowHideSection"
        >
          <template #child>
            <v-row style="padding: 10px; padding-bottom: 0px;">
              <h3 :class="[!summaryReport[sections.analyses] ? 'section--title disabled--text' : 'section--title']">
                {{ $t('forms.labels.analysis') }}
              </h3>
            </v-row>
            <v-row style="padding-bottom: 20px">
              <v-col
                v-if="summaryReport[sections.analyses]"
                cols="12"
                style="padding-top: 0px"
              >
                <MeasuresAndSamplesActivity
                  :allowActionButtonsBreakpoint="false"
                  :tables="tables"
                  @getTables="getTables"
                />
              </v-col>
              <v-col
                v-else
                cols="12"
              >
                <p class="title--hidden">
                  {{ $t('forms.labels.noDataInHiddenMode') }}
                </p>
              </v-col>
            </v-row>
          </template>
        </WorkOrderSection>

        <WorkOrderSection
          v-if="(!hiddenItemsByDefault.showFlowMeter && workOrder.hasFlowMeter) || (!hiddenItemsByDefault.showFuelMeter && workOrder.hasFuelMeter)"
          :iconName="'flowMeter'"
          :sectionName="sections.flowMeter"
          :showReportSection="summaryReport[sections.flowMeter]"
          :isDisabled="isCompletedOrLocked"
          @showHideSection="handleShowHideSection"
        >
          <template #child>
            <v-row
              style="padding: 10px"
              align="center"
              justify="space-between"
            >
              <h3 :class="[!summaryReport[sections.flowMeter] ? 'section--title disabled--text' : 'section--title']">
                {{ $t('devices.meters.name') }}
              </h3>
            </v-row>
            <v-row style="padding-bottom: 20px">
              <v-col
                v-if="summaryReport[sections.flowMeter]"
                cols="12"
                style="padding-top: 0px"
              >
                <WorkOrderFlowMeters

                  :flowmeterNetworks="flowmeterNetworks"
                  :tables="tables"
                  @getMetersByNetwork="getMetersByNetwork"
                />
              </v-col>
              <v-col
                v-else
                cols="12"
              >
                <p class="title--hidden">
                  {{ $t('forms.labels.noDataInHiddenMode') }}
                </p>
              </v-col>
            </v-row>
          </template>
        </WorkOrderSection>
        <!-- Product Level section -->
        <WorkOrderSection
          v-if="!hiddenItemsByDefault.showProductLevel && workOrder.hasProductLevel"
          :iconName="'productLevel'"
          :sectionName="sections.productLevel"
          :showReportSection="summaryReport[sections.productLevel]"
          :isDisabled="isCompletedOrLocked"
          @showHideSection="handleShowHideSection"
        >
          <template #child>
            <v-row
              style="padding: 10px"
              align="center"
              justify="space-between"
            >
              <h3 :class="[!summaryReport[sections.productLevel] ? 'section--title disabled--text' : 'section--title']">
                {{ $t('devices.productLevel.workOrderProductLevel') }}
              </h3>
            </v-row>
            <v-row style="padding-bottom: 20px">
              <v-col
                v-if="summaryReport[sections.productLevel]"
                cols="12"
                style="padding-top: 0px"
              >
                <WorkOrderProductLevel
                  :productmeterNetworks="productmeterNetworks"
                  :tables="tables"
                  @getMetersByNetwork="getMetersByNetwork"
                />
              </v-col>
              <v-col
                v-else
                cols="12"
              >
                <p class="title--hidden">
                  {{ $t('forms.labels.noDataInHiddenMode') }}
                </p>
              </v-col>
            </v-row>
          </template>
        </WorkOrderSection>
        <!-- Mass balance section -->
        <WorkOrderSection
          v-if="!hiddenItemsByDefault.showMassBalance && workOrder.hasMassBalance"
          :iconName="'massBalance'"
          :sectionName="sections.massBalance"
          :showReportSection="summaryReport[sections.massBalance]"
          :isDisabled="isCompletedOrLocked"
          @showHideSection="handleShowHideSection"
        >
          <template #child>
            <v-row
              style="padding: 10px; margin-bottom: 10px"
              align="center"
              justify="space-between"
            >
              <h3 :class="[!summaryReport[sections.analysisResult] ? 'section--title disabled--text' : 'section--title']">
                {{ $t('visitSummary.massBalance') }}
              </h3>
              <v-btn
                elevation="2"
                small
                color="#26384B"
                class="white--text"
                style="text-transform: none"
                :disabled="isCompletedOrLocked"
                @click="updateMassBalance"
              >
                {{ $t('massBalance.sectionWO.updateData') }}
              </v-btn>
            </v-row>
            <div class="massBalanceWo">
              <div
                v-for="( category, index ) in massBalanceCategories"
                :key="index"
              >
                <v-toolbar
                  dense
                  class="tree-toolbar mb-1 toolbarMassBalance"
                  elevation="3"
                >
                  <v-btn
                    icon
                    data-cy="leftArrow"
                    @click="toggleMenuMassBalance(category.toggleName)"
                  >
                    <v-icon
                      large
                      color="#757575"
                    >
                      {{ data.massBalance[category.toggleName] ? 'mdi-menu-down' : 'mdi-menu-right' }}
                    </v-icon>
                  </v-btn>
                  <div
                    class="tree-toolbar__title d-flex"
                  >
                    <div class="font-weight-bold">
                      {{ massBalanceTitle(category.title.toLowerCase()) }}
                    </div>
                    <div class="d-flex pr-4">
                      <div
                        style="height:25px;"
                      >
                        <v-switch
                          :inputValue="showAllMassBalanceOnReport[category.title.toLowerCase()]"
                          color="primary"
                          :disabled="isCompletedOrLocked || updatingPdf"
                          @click.stop="updateShowAllPdfByCategory(workOrder.id, category.title.toLowerCase())"
                        />
                      </div>
                      <div>
                        {{ $t('massBalance.sectionWO.showAllOnPDF') }}
                      </div>
                    </div>
                  </div>

                  <!-- Affichage des carte massBalance en dessous -->
                </v-toolbar>
                <div
                  v-if="data.massBalance[category.toggleName]"
                >
                  <v-col
                    v-if="!data.massBalance.values[category.title.toLowerCase()]"
                    cols="12"
                  >
                    <p class="title--hidden">
                      {{ $t('pagination.noData') }}
                    </p>
                  </v-col>
                  <div class="blocBoilerCards">
                    <div
                      v-for="(child, idx) in data.massBalance.values[category.title.toLowerCase()]"
                      :key="idx + refreshKey"
                    >
                      <MassBalanceSectionSteam
                        v-if="category.title.toLowerCase() === 'steam'"
                        :measureDetails="child"
                        :showOnPdf="data.massBalance.values[category.title.toLowerCase()].showAllPdf"
                        @change="(measureId, valueToggle) => toggleshowPdfInMeasureMassBalance(measureId, valueToggle)"
                        @updateActiveDeactive="updateActiveDeactive"
                      />
                      <Cooling
                        v-if="category.title.toLowerCase() === 'cooling'"
                        :measureDetails="child"
                        :showOnPdf="data.massBalance.values[category.title.toLowerCase()].showAllPdf"
                        @change="(measureId, valueToggle) => toggleshowPdfInMeasureMassBalance(measureId, valueToggle)"
                        @updateActiveDeactive="updateActiveDeactive"
                      />
                      <Simplified
                        v-if="category.title.toLowerCase() === 'simplified'"
                        :measureDetails="child"
                        :workOrder="workOrder"
                        :showOnPdf="data.massBalance.values[category.title.toLowerCase()].showAllPdf"
                        @change="(measureId, valueToggle) => toggleshowPdfInMeasureMassBalance(measureId, valueToggle)"
                        @updateActiveDeactive="updateActiveDeactive"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </template>
        </WorkOrderSection>

        <WorkOrderSection
          v-if="!hiddenItemsByDefault.showSamples"
          iconName="science"
          :sectionName="sections.analysisResult"
          :showReportSection="summaryReport[sections.analysisResult]"
          :isDisabled="isCompletedOrLocked"
          @showHideSection="handleShowHideSection"
        >
          <template #child>
            <v-row
              style="padding: 10px"
              align="center"
              justify="space-between"
            >
              <h3 :class="[!summaryReport[sections.analysisResult] ? 'section--title disabled--text' : 'section--title']">
                {{ $t('visitSummary.analysisRequests') }}
              </h3>
            </v-row>
            <v-row style="padding-bottom: 20px">
              <v-col cols="12">
                <AnalysisRequests
                  v-if="summaryReport[sections.analysisResult]"
                  :workOrderId="workOrder.id"
                  :analysisTypes="analysisTypes"
                  :tree="tree"
                />
                <p
                  v-else
                  class="title--hidden"
                >
                  {{ $t('forms.labels.noDataInHiddenMode') }}
                </p>
              </v-col>
            </v-row>
          </template>
        </WorkOrderSection>

        <WorkOrderSection
          v-if="!hiddenItemsByDefault.showInventory && workOrder.hasInventory"
          iconName="inventory"
          :sectionName="sections.inventory"
          :showReportSection="summaryReport[sections.inventory]"
          :isDisabled="isCompletedOrLocked"
          @showHideSection="handleShowHideSection"
        >
          <template #child>
            <v-row
              style="padding: 10px"
              align="center"
              justify="space-between"
            >
              <h3 :class="[!summaryReport[sections.inventory] ? 'section--title disabled--text' : 'section--title']">
                {{ $t('workOrderPage.inventory') }}
              </h3>
            </v-row>
            <v-row
              justify="center"
              class="pb-5"
            >
              <Inventory
                :disabled="!summaryReport[sections.inventory]"
                :expirationDataCy="dataCy.workOrders.workOrderView.inputs.expiration"
                :quantityDataCy="dataCy.workOrders.workOrderView.inputs.quantity"
              />
            </v-row>
          </template>
        </WorkOrderSection>

        <WorkOrderSection
          v-if="!hiddenItemsByDefault.showAnnexComments"
          iconName="rate_review"
          :sectionName="sections.commentsAndPictures"
          :isDisabled="isCompletedOrLocked"
          :showReportSection="summaryReport[sections.commentsAndPictures]"
          @showHideSection="handleShowHideSection"
        >
          <template #child>
            <v-row style="padding: 10px">
              <h3 :class="[!summaryReport[sections.commentsAndPictures] ? 'section--title disabled--text' : 'section--title']">
                {{ $t('visitSummary.commentsAndPictures') }}
              </h3>
            </v-row>
            <v-row style="padding: 0px 13px 20px 13px">
              <GlobalComment
                ref="commentsAndPictures"
                position="comments"
                :comment="lastCommentAndPicture"
                :isLab="false"
                :workOrderId="workOrder.id"
                :options="{disabled: isCompletedOrLocked || !summaryReport[sections.commentsAndPictures]}"
                :editorDataCy="dataCy.workOrders.workOrderView.inputs.commentAndPic"
                :fileInputDataCy="dataCy.workOrders.workOrderView.inputs.picture"
                :saveBtnDataCy="dataCy.workOrders.workOrderView.buttons.saveComment"
                :filesDataCy="dataCy.workOrders.workOrderView.inputs.commentFiles"
                @comments="getComments"
                @commentChange="(message, isFile) => onCommentChanged(message, 'comments', isFile)"
                @onCommentSaved="unsavedComment = false"
              />
            </v-row>
          </template>
        </WorkOrderSection>

        <WorkOrderSection
          v-if="!hiddenItemsByDefault.showGraphic"
          iconName="poll"
          :isDisabled="isCompletedOrLocked"
          :sectionName="sections.graphs"
        >
          <template #child>
            <v-row style="padding: 10px">
              <h3 :class="[!summaryReport[sections.graphs] ? 'section--title disabled--text' : 'section--title']">
                {{ $t('forms.labels.graphs') }}
              </h3>
            </v-row>
            <v-row style="padding: 0px 10px">
              <v-col cols="12">
                <VFileDragAndDrop
                  :file="newGraphFile"
                  :mimes="['image/png', 'image/jpeg', 'image/jpg', 'image/gif']"
                  :disabled="isCompletedOrLocked || !summaryReport[sections.graphs]"
                  :loading="uploadGraphFileLoading"
                  autoUpload
                  @onChange="file => onDragAndDropChange(file, 'generalGraphs')"
                />
              </v-col>
            </v-row>
            <v-row style="padding: 0px 30px 10px 30px !important">
              <UploadedBackendFiles
                :backendFiles="generalGraphFiles"
                :disabled="isCompletedOrLocked || !summaryReport[sections.graphs]"
                v-on="$listeners"
                @removeFiles="removeFile"
              />
            </v-row>
            <v-row>
              <v-col
                cols="12"
                class="d-flex justify-center"
              >
                <div> {{ $t('graphics.downloadDropDown.or') }} </div>
              </v-col>
            </v-row>
            <v-row style="margin-bottom: 0;">
              <v-col
                cols="12"
                class="d-flex justify-center"
                style="padding-bottom: 4px;"
              >
                <v-btn
                  :color="'grey lighten-2'"
                  class="ma-2"
                  :disabled="isCompletedOrLocked || graphChartFiles.length >= 5"
                  style="min-width: 180px; color: #888; margin-bottom: 0;"
                  @click="showGraphics"
                >
                  <v-icon left>
                    mdi-chart-bar
                  </v-icon>
                  {{ $t('workOrders.graphs.addGraph') }}
                </v-btn>
              </v-col>
            </v-row>
            <v-row style="margin-top: -10px;">
              <v-col
                cols="12"
                class="d-flex justify-center"
                style="padding-top: 0;"
              >
                <span
                  :style="{
                    color: graphChartFiles.length >= 5 ? 'red' : '#aaa',
                    fontSize: '13px'
                  }"
                >{{ $t('workOrders.graphs.addGraphsDescription') }}</span>
              </v-col>
            </v-row>
            <v-row style="padding: 0px 30px 10px 30px !important">
              <DraggableUploadedBackendFiles
                :backendFiles="graphChartFiles"
                :showThumbnail="true"
                :loading="graphImagSpinner"
                :disabled="!summaryReport[sections.graphs]"
                v-on="$listeners"
                @removeFiles="removeFile"
                @reorder="reorderGraphChatsComments"
              />
            </v-row>
          </template>
        </WorkOrderSection>

        <WorkOrderSection
          v-if="!hiddenItemsByDefault.showClosingComments"
          iconName="rate_review"
          :sectionName="sections.closingComments"
          :showReportSection="summaryReport[sections.closingComments]"
          :isDisabled="isCompletedOrLocked"
          @showHideSection="handleShowHideSection"
        >
          <template #child>
            <v-row style="padding: 10px">
              <h3 :class="[!summaryReport[sections.closingComments] ? 'section--title disabled--text' : 'section--title']">
                {{ $t('forms.labels.closingComment') }}
              </h3>
            </v-row>
            <v-row style="padding: 0px 13px 20px 13px">
              <GlobalComment
                ref="closingComment"
                position="closing"
                :comment="lastClosingComment"
                :canUploadImages="false"
                :isLab="false"
                :workOrderId="workOrder.id"
                :options="{disabled: isCompletedOrLocked || !summaryReport[sections.closingComments]}"
                :editorDataCy="dataCy.workOrders.workOrderView.inputs.closingComment"
                :saveBtnDataCy="dataCy.workOrders.workOrderView.buttons.saveComment"
                @commentChange="(message) => onCommentChanged(message, 'closing')"
                @onCommentSaved="unsavedComment = false"
              />
            </v-row>
          </template>
        </WorkOrderSection>

        <WorkOrderSection
          v-if="!hiddenItemsByDefault.showAppendices"
          isLast
          iconName="attach_file"
          :sectionName="sections.appendices"
          :showReportSection="summaryReport[sections.appendices]"
          :isDisabled="isCompletedOrLocked"
          @showHideSection="handleShowHideSection"
        >
          <template #child>
            <v-row style="padding: 10px">
              <h3 :class="[!summaryReport[sections.appendices] ? 'section--title disabled--text' : 'section--title']">
                {{ $t('forms.labels.appendices') }}
              </h3>
            </v-row>
            <v-row style="padding: 0px 10px">
              <v-col cols="12">
                <VFileDragAndDrop
                  :file="newAppendiceFile"
                  :mimes="['application/pdf']"
                  :disabled="isCompletedOrLocked || !summaryReport[sections.appendices]"
                  :loading="uploadFileLoading"
                  :showPdfResizeMsg="true"
                  autoUpload
                  @onChange="file => onDragAndDropChange(file, 'appendix')"
                />
              </v-col>
            </v-row>
            <v-row style="padding: 0px 30px 10px 30px !important">
              <UploadedBackendFiles
                :backendFiles="appendicesFiles"
                type="appendix"
                :disabled="!summaryReport[sections.appendices]"
                :dataCy="dataCy.workOrders.workOrderView.inputs.appendixFiles"
                v-on="$listeners"
                @removeFiles="removeFile"
              />
            </v-row>
          </template>
        </WorkOrderSection>
      </div>
    </div>
    <ConfirmModal
      v-if="!verifyIsCompleted"
      titleKey="visitSummary.approve"
      contentKey="visitSummary.submitConfirmation"
      :showConfirmBox="showConfirmSubmit"
      @hide="showConfirmSubmit = false"
      @destroyModal="onConfirmSubmit"
    />
    <!-- unsaved modal for unsaved comments -->
    <UnsavedConfirmModal
      :showUnsavedDialog="showUnsavedDialog"
      :onApprovePress="onApprovePress"
      @exitWithoutSaving="exitWithoutSaving"
      @saveAndExit="saveAndExit"
      @close="closeUnsavedDialog"
    />
    <LoadingWidget
      :show="showLoadingWidget"
    />
  </div>
</template>

<script>
import Vue from 'vue';
import moment from 'moment-timezone';
import { mapActions, mapGetters, mapMutations } from 'vuex';
import WorkOrderForm from '@/components/forms/WorkOrderForm.vue';
import WorkOrderForm2 from '@/components/forms/WorkOrderForm2.vue';
import WorkOrderSection from '@/views/workOrders/overview/WorkOrderSection';
import completionStatus from '@/constants/workOrderStatusChoices';
import GlobalComment from '@/views/laboratory/workOrder/summary/GlobalComment';
import { last, sortBy, debounce, cloneDeep } from 'lodash';
import ActionBar from '@/views/workOrders/shared/ActionBar.vue';
import ConfirmModal from '@/components/shared/forms-controls/ConfirmModal';
import UnsavedConfirmModal from '@/components/shared/forms-controls/UnsavedConfirmModal';
import LoadingWidget from '@/components/shared/widgets/LoadingWidget';
import Inventory from '@/views/workOrders/activity/Inventory.vue';
import UploadedBackendFiles from '@/components/shared/UploadedBackendFiles.vue';
import DraggableUploadedBackendFiles from '@/components/shared/DraggableUploadedBackendFiles.vue';
import VFileDragAndDrop from '@/components/shared/widgets/forms/VuetifyInputs/VFileDragAndDrop';
import MeasuresAndSamplesActivity from '@/views/workOrders/activity/MeasuresAndSamplesActivity.vue';
import VAutocomplete from '@/components/shared/widgets/forms/VuetifyInputs/VAutocomplete';
import AnalysisRequests from '@/views/workOrders/shared/AnalysisRequests.vue';
import summarySections from '@/constants/summaryReports';
import { workOrders } from '@/aq-data-cy.json';
import WorkOrderFlowMeters from '@/views/workOrders/flowMeters/WorkOrderFlowMeters.vue';
import WorkOrderProductLevel from '@/views/workOrders/productLevel/WorkOrderProductLevel.vue';
import { SOURCE_MANUAL } from '@/aquassist-shared/constants/types/flowMeters';
import WorkOrderGraphForm from '@/views/workOrders/WorkOrderGraphForm.vue';
import { convertSvgElementToPngFile } from '@/utilities/graphs';
import MassBalanceSectionSteam from './massBalanceSection/Steam.vue';
import Simplified from './massBalanceSection/Simplified.vue';
import Cooling from './massBalanceSection/Cooling.vue';
import { COOLING, STEAM } from '../../aquassist-shared/constants/types/network';


export default {
    components: {
        WorkOrderProductLevel,
        WorkOrderFlowMeters,
        WorkOrderForm,
        WorkOrderSection,
        WorkOrderForm2,
        GlobalComment,
        DraggableUploadedBackendFiles,
        UploadedBackendFiles,
        ActionBar,
        ConfirmModal,
        UnsavedConfirmModal,
        LoadingWidget,
        Inventory,
        VFileDragAndDrop,
        MeasuresAndSamplesActivity,
        VAutocomplete,
        AnalysisRequests,
        MassBalanceSectionSteam,
        Cooling,
        Simplified,
    },
    data() {
        return {
            comments: [],
            appendicesFiles: [],
            generalGraphFiles: [],
            graphChartFiles: [],
            hasNotType: false,
            isReopened: false,
            localFiles: [],
            loading: true,
            graphImagSpinner: false,
            newAppendiceFile: null,
            newGraphFile: null,
            nextRouteAfterSave: null,
            onApprovePress: false,
            refreshKey: 0,
            reportTypeLoading: false,
            showConfirmSubmit: false,
            showUnsavedDialog: false,
            showLoadingWidget: false,
            unsavedComment: false,
            verifyIsCompleted: false,
            data: {
                massBalance: {
                    coolingToolbarDeployed: false,
                    steamToolbarDeployed: false,
                    simplifiedToolbarDeployed: false,
                    values: {
                        showAllPdf: {
                            cooling: false,
                            steam: false,
                        },
                        steam: [],
                        cooling: [],
                        simplified: [],
                    },
                },
            },
            debouncedInput: debounce(this.onInput, 500),
            reportTypes: [],
            uploadFileLoading: false,
            uploadGraphFileLoading: false,
            summaryReport: {},
            flowmeterNetworks: [],

            productmeterNetworks: [],
            tables: null,
            hiddenItemsByDefault: {
                showOpeningComments: true,
                showAnalyses: true,
                showSamples: true,
                showInventory: true,
                showAnnexComments: true,
                showClosingComments: true,
                showAppendices: true,
                showFlowmeter: true,
                showProductLevel: true,
                showMassBalance: true,
                showFuelMeter: true,
            },
            showAllMassBalanceOnReport: {
                cooling: false,
                steam: false,
            },
            workOrderType: null,
            updatingPdf: false,
        };
    },
    computed: {
        ...mapGetters([
            'workOrder',
            'lang',
            'teamMainLang',
            'roles',
        ]),
        COOLING() {
            return COOLING;
        },
        title() {
            const { site } = this.workOrder || {};

            return site ? `${site.client.name} [${site.client.accountNumber}]` : null;
        },
        isCompletedOrLocked() {
            return this.workOrder?.completionStatus === completionStatus.COMPLETED || !!this.workOrder?.lockData?.isLocked;
        },
        lastOpeningComment() {
            const comments = this.comments.filter(item => item.position === 'opening');
            return comments[comments.length - 1];
        },
        lastCommentAndPicture() {
            const comments = this.comments.filter(item => item.position === 'comments');
            return comments[comments.length - 1] || {};
        },
        lastClosingComment() {
            const comments = this.comments.filter(item => item.position === 'closing');
            return comments[comments.length - 1] || {};
        },
        isCompleted() {
            return this.workOrder?.completionStatus === completionStatus.COMPLETED;
        },
        isLocked() {
            return !!this.workOrder?.lockData?.isLocked;
        },
        reports() {
            return this.workOrder?.reports || [];
        },
        lastReport() {
            if (!Array.isArray(this.reports) || this.reports.length === 0) {
                return null;
            }

            // Sort by creation date
            const sorted = sortBy(this.reports, report => (new Date(report.createdAt)));

            return last(sorted);
        },
        canLeave() {
            const { onLocationFrom, onLocationTo } = this.workOrder || {};

            return (onLocationFrom && !onLocationTo);
        },
        displayedReportTypes() {
            const [role] = Object.values(this.roles);
            const allowedTypes = this?.reportTypes.map(type => {
                if (type.roles.find(r => r.key === role.key)) return type;
                return null;
            }).filter(Boolean);

            if (allowedTypes?.length === 0) return [];

            if (!this.data.type && allowedTypes?.length === 1) {
                const [reportType] = allowedTypes;
                // eslint-disable-next-line vue/no-side-effects-in-computed-properties
                this.data.type = reportType;
            }
            return allowedTypes?.map(reportType => ({
                id: reportType.id,
                name: reportType.label ? reportType.label[this.lang] || reportType.label[this.teamMainLang] : '',
            }));
        },
        activity() {
            return this.workOrder?.activities?.measuresAndSamplesCollect;
        },
        tree() {
            return this.activity?.tree;
        },
        analysisTypes() {
            return this.activity?.analysisTypes;
        },
        sections() {
            return summarySections;
        },
        dataCy() {
            return {
                workOrders,
            };
        },
        massBalanceCategories() {
            const cat = [
                {
                    title: 'Cooling',
                    toggleName: 'coolingToolbarDeployed',
                },
                {
                    title: 'Steam',
                    toggleName: 'steamToolbarDeployed',
                },
                {
                    title: 'simplified',
                    toggleName: 'simplifiedToolbarDeployed',
                },
            ];
            if (this.isNotProduction) {
                cat.push({
                    title: 'simplified',
                    toggleName: 'simplifiedToolbarDeployed',
                });
            }
            return cat;
        },
        isNotProduction() {
            return process.env.NODE_ENV !== 'production';
        },
    },
    created() {
        if (this.workOrder?.id) {
            this.activityId = this.workOrder.activities?.measuresAndSamplesCollect?.id;
            this.getTables();
            Promise.all([
                this.getInventoryActivity(this.workOrder.id),
                this.getComments(),
                this.getReportTypes(),
                this.getMetersByNetwork(),
            ]);
            this.data.name = this.workOrder?.summaryReport.name;
            this.data.type = this.workOrder?.summaryReport.type;
            this.data.workOrderType = this.workOrder?.workOrderType?.name[this.lang] || this.workOrder?.workOrderType?.name[this.teamMainLang];
            this.summaryReport = this.workOrder?.summaryReport;
            this.showAllMassBalanceOnReport = this.workOrder?.showAllMassBalanceOnReport || false;

            this.getMassBalanceCalculatedValues();
        }

        this.dontDisplayHiddenItemsByDefault();
    },
    mounted() {
    // Warn on browser/tab close or reload
        // eslint-disable-next-line consistent-return
        this.beforeUnloadHandler = e => {
            if (this.unsavedComment) {
                this.showUnsavedDialog = true;
                e.preventDefault();
                // eslint-disable-next-line no-param-reassign
                e.returnValue = '';
                return '';
            }
        };
        return window.addEventListener('beforeunload', this.beforeUnloadHandler);
    },
    // Warn on route leave
    beforeRouteLeave(to, from, next) {
        if (this.unsavedComment) {
            this.showUnsavedDialog = true;
            // Don't navigate away and save the rout user click
            this.nextRouteAfterSave = to;

            return;
        }
        next();
    },
    beforeDestroy() {
        window.removeEventListener('beforeunload', this.beforeUnloadHandler);
    },
    methods: {
        ...mapActions({
            reloadWorkOrder: 'getWorkOrderInfo',
            getInventoryActivity: 'getInventoryActivity',
        }),
        ...mapMutations([
            'updateSummaryReport',
        ]),
        massBalanceTitle(title) {
            switch (title) {
                case COOLING:
                    return this.$t('massBalance.coolingTowerSection.name');
                case STEAM:
                    return this.$t('massBalance.boilerSection');
                case 'simplified':
                    return this.$t('massBalance.simplified.title');
                default:
                    return '';
            }
        },
        async updateActiveDeactive() {
            await this.getMassBalanceCalculatedValues();
            this.reloadWorkOrder(this.workOrder.id);
            this.refreshKey += 1;
        },
        toggleMenuMassBalance(menuName) {
            if (menuName === 'coolingToolbarDeployed') this.data.massBalance.coolingToolbarDeployed = !this.data.massBalance.coolingToolbarDeployed;
            if (menuName === 'steamToolbarDeployed') this.data.massBalance.steamToolbarDeployed = !this.data.massBalance.steamToolbarDeployed;
            if (menuName === 'simplifiedToolbarDeployed') this.data.massBalance.simplifiedToolbarDeployed = !this.data.massBalance.simplifiedToolbarDeployed;
        },
        async getComments() {
            const { data } = await this.$rq.getComments(this.workOrder.id);
            this.comments = data;
            this.appendicesFiles = data
                .filter(comment => comment.position === 'appendix' && comment.files?.length)
                .map(comment => ({ ...comment.files[0] }));
            this.generalGraphFiles = data
                .filter(comment => comment.position === 'generalGraphs' && comment.files?.length)
                .map(comment => ({ ...comment.files[0] }));
            this.graphChartFiles = data
                .filter(comment => comment.position === 'graphCharts' && comment.files?.length)
                .sort((a, b) => (a.order || 0) - (b.order || 0))
                .map(comment => ({ ...comment.files[0] }));
        },

        async handleFile(files, position) {
            const promises = [];
            files.forEach(file => {
                const formData = new FormData();
                formData.append('file', file);
                const title = file.name.split('.pdf')[0];
                promises.push(this.addFile(formData, position, title));
            });
            await Promise.all(promises);
        },
        async addFile(formData, position, title = '') {
            const file = await this.$rq.addFile({ resizePdf: true }, formData);
            const payload = {
                fileId: file.data.id,
                title,
                position,
            };
            if (position === 'graphCharts') {
                payload.order = this.getLastGraphChartCommentOrder();
            }
            await this.$rq.addComment(this.workOrder.id, payload);

            await this.getComments();
        },
        getLastGraphChartCommentOrder() {
            const graphChartComments = this.comments.filter(comment => comment.position === 'graphCharts' && comment.files?.length);
            if (!graphChartComments.length) return 1;
            // Find the comment with highest order value
            const highestOrderComment = graphChartComments.reduce((highest, current) => {
                const currentOrder = current.order || 0;
                const highestOrder = highest.order || 0;
                return currentOrder > highestOrder ? current : highest;
            }, { order: 0 });

            // Return the highest order + 1
            return (highestOrderComment.order || 0) + 1;
        },
        async onClickOnLocation() {
            const { id } = this.workOrder || {};
            const payload = {};
            const now = moment(new Date());
            if (this.canLeave) {
                payload.onLocationFrom = this.workOrder.onLocationFrom;
                payload.onLocationTo = now;
            } else {
                payload.onLocationTo = null;
                payload.onLocationFrom = now;
            }
            await this.$rq.updateWorkOrder(id, payload);
            await this.reloadWorkOrder(id);
        },
        async checkIsReopened(val) {
            this.isReopened = val;
            if (this.isReopened) {
                const { id } = this.workOrder || {};
                const payload = {};
                payload.completionStatus = completionStatus.IN_PROGRESS;
                await this.$rq.reopenWorkOrder(id);
                await this.$rq.updateWorkOrder(id, payload);
                await this.reloadWorkOrder(id);
                this.isReopened = false;
            }
        },
        checkIsCompleted(val) {
            this.verifyIsCompleted = val;
        },
        async onConfirmSubmit() {
            this.showConfirmSubmit = false;
            this.showLoadingWidget = true;
            this.onApprovePress = true;

            try {
                const { id, summaryReport } = this.workOrder || {};
                if (summaryReport.type) {
                    this.hasNotType = false;
                    await this.$rq.submitWorkOrder(id);
                    await this.reloadWorkOrder(id);

                    this.$toasted.global.toast_success({
                        message: this.$t('visitSummary.submitSuccess'),
                    });
                    this.isReopened = true;
                } else {
                    this.hasNotType = true;
                }
            } catch (error) {
                this.$toasted.global.toast_error({
                    message: error,
                });
            }

            this.showLoadingWidget = false;
            this.onApprovePress = false;
        },
        async onDragAndDropChange(file, position) {
            if (!position) return;
            if (position === 'generalGraphs') {
                this.uploadGraphFileLoading = true;
                this.newGraphFile = file;
            } else if (position === 'appendix') {
                this.newAppendiceFile = file;
                this.uploadFileLoading = true;
            }
            await this.handleFile([file], position);
            await this.getComments();
            this.uploadFileLoading = false;
            this.uploadGraphFileLoading = false;
            this.newAppendiceFile = null;
            this.newGraphFile = null;
        },
        async removeFile(fileId) {
            this.newAppendiceFile = null;
            let correspondingComment = {};
            this.comments.every(comment => {
                // get comment based on fileId
                if (comment?.files && comment?.files?.find(file => file.id === fileId)) {
                    correspondingComment = comment;
                    return false;
                }
                return true;
            });
            await this.$rq.deleteFile(fileId);
            await this.$rq.deleteComment(this.workOrder.id, correspondingComment.id);
            await this.getComments();
        },
        async getReportTypes() {
            this.reportTypeLoading = true;
            const { data } = await this.$rq.getReportTypes({ pagination: false });
            const [role] = Object.values(this.roles);
            this.reportTypes = data.map(type => {
                if (type.roles.find(r => r.key === role.key)) return type;
                return null;
            }).filter(Boolean);
            this.reportTypeLoading = false;
        },
        async onInput(key) {
            const value = this.data[key];
            const { id } = this.workOrder || {};
            // API call
            await this.$rq.updateSummaryReport(id, { [key]: value });
            // Store update
            await this.updateSummaryReport({ [key]: value });
            await this.reloadWorkOrder(id);
        },
        async handleShowHideSection(value, section) {
            const { id } = this.workOrder || {};
            if (id) {
                // API call
                await this.$rq.updateSummaryReport(id, { [section]: value });

                // Store update
                await this.updateSummaryReport({ [section]: value });
                await this.reloadWorkOrder(id);

                // UI - update
                this.summaryReport = this.workOrder?.summaryReport;
            }
        },


        async getMetersByNetwork() {
            const { data } = await this.$rq.getDevicesByNetwork(this.activityId);
            const flowmeterNetworks = data.devices?.flowmeters?.map(f => ({
                ...f,
                meters: f.meters.map(m => ({
                    ...m,
                    value: m.dataSource === SOURCE_MANUAL ? m.measureValue : Number(m.measureValue),
                    dailyVolumeConsumption: this.$t('devices.historicalConsumption'),
                    lastReadingDate: this.$options.filters.formatDate(m.lastReadingDate || m.updatedAt, 'L LT'),
                    treeOrder: m.component.treeOrder,
                    previousMeasureValue: m && m.previousMeasureValue,
                })),
            }));
            const fuelmeterNetworks = data.devices?.fuelmeters?.map(f => ({
                ...f,
                meters: f.meters.map(m => ({
                    ...m,
                    value: m.dataSource === SOURCE_MANUAL ? m.measureValue : Number(m.measureValue),
                    dailyVolumeConsumption: this.$t('devices.historicalConsumption'),
                    lastReadingDate: this.$options.filters.formatDate(m.lastReadingDate || m.updatedAt, 'L LT'),
                    treeOrder: m.component.treeOrder,
                    previousMeasureValue: m && m.previousMeasureValue,
                })),
            }));
            if (fuelmeterNetworks?.length) {
                fuelmeterNetworks.forEach(fuelNetwork => {
                    const existingNetwork = flowmeterNetworks.find(flowNetwork => flowNetwork.id === fuelNetwork.id);
                    if (existingNetwork) {
                        existingNetwork.meters.push(...fuelNetwork.meters);
                    } else {
                        flowmeterNetworks.push(fuelNetwork);
                    }
                });
            }
            const flowmeterRateNetworks = data.devices?.flowmeterRate?.map(f => ({
                ...f,
                meters: f.meters.map(m => ({
                    ...m,
                    flowMeterName: m.name,
                    value: m.dataSource === SOURCE_MANUAL ? m.measureValue : Number(m.measureValue),
                    dailyVolumeConsumption: this.$t('devices.historicalConsumption'),
                    lastReadingDate: this.$options.filters.formatDate(m.lastReadingDate || m.updatedAt, 'L LT'),
                    treeOrder: m.component.treeOrder,
                    previousMeasureValue: m && m.previousMeasureValue,
                })),
            }));
            if (flowmeterRateNetworks?.length) {
                flowmeterRateNetworks.forEach(rateNetwork => {
                    const existingNetwork = flowmeterNetworks.find(flowNetwork => flowNetwork.id === rateNetwork.id);
                    if (existingNetwork) {
                        existingNetwork.meters.push(...rateNetwork.meters);
                    } else {
                        flowmeterNetworks.push(rateNetwork);
                    }
                });
            }

            this.flowmeterNetworks = flowmeterNetworks;

            this.productmeterNetworks = data.devices.productlevels.map(f => ({
                ...f,
                meters: f.meters.map(m => ({
                    ...m,
                    productName: m.product.name,
                    value: m.dataSource === SOURCE_MANUAL ? m.measureValue : Number(m.measureValue),
                    dailyVolumeConsumption: this.$t('devices.historicalConsumption'),
                    lastReadingDate: this.$options.filters.formatDate(m.lastReadingDate || m.updatedAt, 'L LT'),
                    treeOrder: m.component.treeOrder,
                    previousMeasureValue: m && m.previousMeasureValue,
                })),
            }));
        },
        async getTables() {
            const { data } = await this.$rq.getTables(this.activityId);
            this.tables = data.tables;
        },
        dontDisplayHiddenItemsByDefault() {
            if (this.hiddenItemsByDefault) {
                Object.assign(this.hiddenItemsByDefault, this.workOrder.hiddenItemsByDefault);
            }
        },

        async getMassBalanceCalculatedValues() {
            const { data } = await this.$rq.getCalculatedMassBalance(this.workOrder.id);
            this.data.massBalance.values = data;
        },
        async updateMassBalance() {
            const { data } = await this.$rq.postCalculatedMassBalance(this.workOrder.id);
            this.data.massBalance.values = data;
            await this.reloadWorkOrder(this.workOrder.id);
        },
        async toggleshowPdfInMeasureMassBalance(massBalanceId, showPdf) {
            const obj = { showOnReport: showPdf };
            await this.$rq.toggleshowPdfInMeasureMassBalance(this.workOrder.id, massBalanceId, obj);
            await this.reloadWorkOrder(this.workOrder.id);
        },
        async updateShowAllPdfByCategory(workOrderId, category) {
            try {
                this.updatingPdf = true;
                const showAllMassBalanceOnReport = {
                    ...this.showAllMassBalanceOnReport,
                    [category]: !this.showAllMassBalanceOnReport[category],
                };
                const response = await this.$rq.toggleShowAllPdfInMassBalanceCategory(workOrderId, showAllMassBalanceOnReport);
                this.showAllMassBalanceOnReport = response.data.showAllMassBalanceOnReport;
                this.updatingPdf = false;
            } catch (error) {
                this.updatingPdf = false;
                this.$toasted.global.toast_error({ message: error });
            }
        },
        onClickSubmit() {
            if (this.unsavedComment) {
                this.showUnsavedDialog = true;
                this.onApprovePress = true;
            } else {
                this.showConfirmSubmit = true;
            }
        },
        onCommentChanged(newComment, position, isFile = false) {
            let currentComment = null;
            switch (position) {
                case 'opening':
                    currentComment = this.lastOpeningComment;
                    break;
                case 'closing':
                    currentComment = this.lastClosingComment;
                    break;
                case 'comments':
                    currentComment = this.lastCommentAndPicture;
                    break;
                default:
                    break;
            }
            if (isFile) {
                if (newComment && newComment.length > 0) {
                    this.unsavedComment = true;
                }
            } else if (newComment && newComment !== currentComment?.message) {
                this.unsavedComment = true;
            } else {
                this.unsavedComment = false;
            }
        },
        // If comment is not saved and user wants to exit the page
        async saveAndExit() {
            // Save comments
            if (this.$refs?.openingComment?.message || this.$refs.openingComment?.selectedImages?.length > 0) {
                this.$refs.openingComment.onSaveClick(this.workOrderId);
            }
            if (this.$refs?.closingComment?.message || this.$refs.closingComment?.selectedImages?.length > 0) {
                this.$refs.closingComment.onSaveClick(this.workOrderId);
            }
            if (this.$refs?.commentsAndPictures?.message || this.$refs.commentsAndPictures?.selectedImages?.length > 0) {
                this.$refs.commentsAndPictures.onSaveClick(this.workOrderId);
            }
            if (this.onApprovePress) {
                if (this.data.type) {
                    await this.onConfirmSubmit();
                    await this.reloadWorkOrder(this.workOrder.id);
                } else {
                    this.hasNotType = true;
                }
            }
            this.showUnsavedDialog = false;
            this.unsavedComment = false;
            this.onApprovePress = false;
            this.routeAfterUnsavedPrompt();
        },
        async exitWithoutSaving() {
            // For approve without saving
            if (this.onApprovePress) {
                await this.onConfirmSubmit();
                await this.reloadWorkOrder(this.workOrder.id);
            }
            this.showUnsavedDialog = false;
            this.unsavedComment = false; // Allow navigation/close/reload
            this.onApprovePress = false;
            this.routeAfterUnsavedPrompt();
        },
        routeAfterUnsavedPrompt() {
            if (this.nextRouteAfterSave) {
                this.$router.push(this.nextRouteAfterSave.fullPath);
                this.nextRouteAfterSave = null; // Reset next route after navigation
            } else this.$router.back();
        },
        closeUnsavedDialog() {
            this.showUnsavedDialog = false;
            this.unsavedComment = false;
            this.onApprovePress = false;
        },
        // Show graphics modal
        showGraphics() {
            this.$root.$emit('showModal', {
                title: this.$t('workOrders.graphs.graphs'),
                component: Vue.component('WorkOrderGraphForm', WorkOrderGraphForm),
                payload: { workOrderId: this.workOrderId },
                onConfirm: requestPayload => { this.saveGraphic(requestPayload); },
            });
        },
        async saveGraphic(GraphPayload) {
            let payload = cloneDeep(GraphPayload);
            if (!GraphPayload?.length) return;
            this.graphImagSpinner = true;
            const promises = [];
            // Just 5 graph charts are allowed
            if (GraphPayload.length + this.graphChartFiles.length > 5) {
                const allowedNumber = 5 - this.graphChartFiles.length;
                payload = GraphPayload.slice(0, allowedNumber);
            }
            payload.forEach(graph => {
                promises.push(convertSvgElementToPngFile(graph.graph, graph.graphName));
            });
            const filesToUpload = await Promise.all(promises);
            await this.handleFile(filesToUpload, 'graphCharts');
            this.graphImagSpinner = false;
        },
        async reorderGraphChatsComments(filesInOrder) {
            const updatePromises = [];
            const graphChartComments = this.comments.filter(comment => comment.position === 'graphCharts' && comment.files?.length);

            // Loop through files in new order
            filesInOrder.forEach((file, index) => {
                // Find the comment that contains this file
                const commentToUpdate = graphChartComments.find(
                    comment => comment.files[0].id === file.id,
                );

                if (commentToUpdate) {
                    // Update the comment with new order (index + 1)
                    updatePromises.push(
                        this.$rq.editComment(
                            this.workOrder.id,
                            commentToUpdate.id,
                            { order: index + 1 },
                        ),
                    );
                }
            });

            // Wait for all updates to complete
            await Promise.all(updatePromises);
            await this.getComments();
        },
    },
    async beforeRouteUpdate(to, from, next) {
        if (from.params.workOrderId !== to.params.workOrderId) {
            await this.reloadWorkOrder(to.params.workOrderId);
        }
        next();
    },


};
</script>
<style lang="scss" scoped>
@import "@/assets/styles/utils/_variables.scss";
    .contract--number {
      font-size: 30px;
      font-weight: 700;
      color: #26384B;
    }
    .title--hidden {
      font-style: italic;
      font-weight: 400;
      font-size: 16px;
      line-height: 19px;
      text-align: center;
      color: rgba(38, 56, 75, 0.4);
      margin-bottom: 0;
    }

    .tree-toolbar {
      width: 100% !important;
      display: flex !important;
      flex-direction: row !important;
      justify-content: space-between !important;
      align-items: center !important;
        border-left: 8px solid $primary-color;
        border-radius: 3px;
      &.theme--light {
        .v-toolbar__content {
          .v-toolbar__items {
            .v-icon {
              color: #fff;
            }
          }
        }
      }
      &__title {
        display: flex !important;
        flex-direction: row !important;
        justify-content: space-between !important;
        width: 100% !important;
        margin-left: 20px !important;
      }
    }

    .massBalanceWo{
        display: grid;
        row-gap: .2rem;
        padding-bottom: 1rem;
    }
    ::v-deep .toolbarMassBalance .v-toolbar__content {
        min-width : 100% !important;
        padding: 0 3px;
    }
    .tree-toolbar__title{
        margin: 0 !important;
    }
    .blocBoilerCards{
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        padding: 1rem 0;
        gap: 1.3rem;
    }
    @media (max-width: 1024px) {
        .blocBoilerCards {
            grid-template-columns: repeat(2, 1fr);
        }
    }
    @media (max-width: 768px) {
        .blocBoilerCards {
            grid-template-columns: 1fr;
        }
    }
  </style>
